import { useMemo, useState } from "react";
import { HoldoutData } from "@src/types/featureSwitches";
import { LocalStorageWrapper } from "@src/utils/localStorage";

interface FeatureSwitchConfigShape<T extends string> {
  name: T;
  value: string;
  predefinedValues: string[];
}

interface FeatureSwitchesSectionVMProps {
  featureSwitches: FeatureSwitchConfigShape<string>[];
  holdoutData: HoldoutData | undefined;
}

const handleError = (msg: string) => {
  if (process.env.NODE_ENV !== "production") {
    throw new Error(msg);
  } else {
    // eslint-disable-next-line no-console
    console.error(msg);
  }
};

// Simple localStorage hook for search and collapsed state
function useLocalStorage<T>(key: string, initialValue: T): [T, (value: T) => void] {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = LocalStorageWrapper.getItem(key);
      return item ? (JSON.parse(item) as T) : initialValue;
    } catch (error) {
      return initialValue;
    }
  });

  const setValue = (value: T) => {
    try {
      setStoredValue(value);
      LocalStorageWrapper.setItem(key, JSON.stringify(value));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Error saving to localStorage key "${key}":`, error);
    }
  };

  return [storedValue, setValue];
}

// optimization for shifting holdout <-> experiment relation with the minimal amount of loops
function getHoldoutSwitches(featureSwitches: FeatureSwitchConfigShape<string>[], holdoutData: HoldoutData | undefined) {
  if (!holdoutData) return undefined;
  const switchesByExperimentName = featureSwitches.reduce<Record<string, FeatureSwitchConfigShape<string>>>(
    (acc, fs) => {
      acc[fs.name] = fs;
      return acc;
    },
    {},
  );

  const holdoutSwitches = Object.entries(holdoutData).reduce<
    Record<string, { value: FeatureSwitchConfigShape<string>; fs: FeatureSwitchConfigShape<string>[] }>
  >((acc, [experimentInHoldoutName, holdoutInfo]) => {
    const holdoutName = holdoutInfo.holdoutName;
    if (!acc[holdoutName]) {
      acc[holdoutName] = {
        value: switchesByExperimentName[holdoutName],
        fs: switchesByExperimentName[experimentInHoldoutName]
          ? [switchesByExperimentName[experimentInHoldoutName]]
          : [],
      };
    } else {
      acc[holdoutInfo.holdoutName].fs.push(switchesByExperimentName[experimentInHoldoutName]);
    }
    return acc;
  }, {});
  return holdoutSwitches;
}

export function useFeatureSwitchesSectionVM(props: FeatureSwitchesSectionVMProps) {
  const [search, setSearch] = useLocalStorage("DevToolsFeatureSwitchSearch", "");
  const [isCollapsed, setIsCollapsed] = useLocalStorage("DevToolsFeatureSwitchesCollapsed", false);

  const filteredFeatureSwitches = useMemo(() => {
    const holdoutKeys = props.holdoutData ? Object.keys(props.holdoutData) : [];
    const filteredByHoldout = props.featureSwitches.filter((fs) => !holdoutKeys.includes(fs.name));
    const filteredBySearch = filteredByHoldout.filter(
      (fs) => !search || fs.name.toLowerCase().includes(search.toLowerCase()),
    );
    return filteredBySearch;
  }, [props.featureSwitches, props.holdoutData, search]);

  const holdoutFeatureSwitches = useMemo(() => {
    if (!props.holdoutData) return {};

    const groupedByHoldout: Record<
      string,
      {
        value: FeatureSwitchConfigShape<string>;
        fs: FeatureSwitchConfigShape<string>[];
      }
    > = {};

    const holdoutSwitches = getHoldoutSwitches(props.featureSwitches, props.holdoutData);

    for (const [, holdoutInfo] of Object.entries(props.holdoutData)) {
      const holdoutName = holdoutInfo.holdoutName;
      if (!groupedByHoldout[holdoutName] && holdoutSwitches?.[holdoutName]) {
        const holdoutSwitch = holdoutSwitches[holdoutName].value;
        if (!holdoutSwitch) {
          handleError(`Holdout switch not found: ${holdoutName}`);
          continue;
        }

        groupedByHoldout[holdoutName] = {
          value: { ...holdoutSwitch, predefinedValues: ["baseline", "off", "shipped_improvements"] },
          fs: holdoutSwitches[holdoutName].fs,
        };
      }
    }

    return groupedByHoldout;
  }, [props.featureSwitches, props.holdoutData]);

  return {
    search,
    setSearch,
    isCollapsed,
    toggleCollapsed: () => setIsCollapsed(!isCollapsed),
    filteredFeatureSwitches,
    holdoutFeatureSwitches,
  };
}
